// src/components/dentally/DentallyPatientLinkSection.jsx
//
// Small "Dentally patient" card shown above the Personal details form when the
// route practice has an active Dentally integration and the user may view it.
//
// Shows the practice/lead-scoped patient link (identity only — never worded as
// synced), the link source and the link creation time ("Linked on", not
// "Last synced"). Actions are limited to what exists today:
//   - Find and link existing patient (manage access, authoritative "no link")
//     → existing DentallyPatientMapper in a dialog.
//   - Retry check (fetch error).
// Demographic comparison lives in the "Details from Dentally" section below.
// Directional updates CRM -> Dentally now exist (dentalcrm #6700): contact
// details travel on save, name and date of birth ask first.
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import Iconify from '../Iconify';
import DentallyPatientMapper from './DentallyPatientMapper';
import DentallyPatientSummary from './DentallyPatientSummary';
import { archivedPatientMessage } from '../../utils/dentallyPatientSummary';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';
import {
  formatPatientLinkSource,
  formatPatientLinkedOn,
  getPatientLinkPresentation,
  TONE_COLOURS,
} from '../../utils/dentallyPatientLinkStatus';

// Was "saved to the CRM only ... not updated from here yet", which stopped
// being true when the CRM -> Dentally patient push shipped (dentalcrm #6700).
// Contact details now travel on save; a name or date of birth asks first,
// because those are the fields Dentally matches patients on.
const CRM_ONLY_COPY = 'Contact details saved here are sent to Dentally. Changes to the name or date of birth ask before updating the patient record.';

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88 }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
}
DetailRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };
DetailRow.defaultProps = { value: null };

export default function DentallyPatientLinkSection({
  practiceId, leadId, link, outcome, isLoading, canManage, onRefresh, summary, checkedAt,
}) {
  const [mapperOpen, setMapperOpen] = useState(false);
  const presentation = getPatientLinkPresentation(isLoading ? 'loading' : outcome);
  // An archived Dentally patient used to look identical to a healthy one. It is
  // a modifier on the existing connection chip, not a row of its own: the state
  // of the link is what the chip is already there to say.
  const archived = presentation.key === 'linked' && summary?.active === false;
  const chipLabel = archived ? 'Dentally patient archived' : presentation.label;
  const chipIcon = archived ? 'mdi:archive-outline' : presentation.icon;
  const colours = TONE_COLOURS[archived ? 'warning' : presentation.tone];
  const canLink = canManage && presentation.key === 'unlinked' && practiceId && leadId;

  const handleMapped = () => {
    setMapperOpen(false);
    onRefresh?.();
  };

  return (
    <Box sx={{ ...sectionCardStyle, mb: 3 }} data-testid="dentally-patient-link-section" data-state={presentation.key}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={sectionLabelStyle}>DENTALLY PATIENT</Typography>
        <Chip
          size="small"
          icon={<Iconify icon={chipIcon} width={16} height={16} />}
          label={chipLabel}
          sx={{ bgcolor: colours.bg, color: colours.fg, fontWeight: 600, '& .MuiChip-icon': { color: colours.fg } }}
        />
      </Box>

      {presentation.key === 'linked' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1.5 }}>
          <DetailRow label="Patient ID" value={`#${link?.dentally_patient_id}`} />
          <DetailRow label="Connection" value={formatPatientLinkSource(link?.source)} />
          <DetailRow label="Linked on" value={formatPatientLinkedOn(link?.created_at)} />
        </Box>
      )}

      {archived && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="dentally-patient-archived">
          {archivedPatientMessage(summary)}
        </Alert>
      )}

      {presentation.key === 'linked' && (
        <DentallyPatientSummary summary={summary} checkedAt={checkedAt} />
      )}

      {presentation.key === 'unlinked' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          No Dentally patient is linked to this lead. A patient is created or linked when a Dentally booking is made.
        </Typography>
      )}

      {presentation.key === 'attention' && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Patient link needs review — the link for this lead is ambiguous or unreadable. Resolve it before
          exporting or booking this patient in Dentally.
        </Alert>
      )}

      {presentation.key === 'error' && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Dentally could not be checked just now. This does not mean the patient is unlinked.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        {canLink && (
          <Button size="small" variant="outlined" startIcon={<Iconify icon="mdi:link-variant" />} onClick={() => setMapperOpen(true)}>
            Find and link existing patient
          </Button>
        )}
        {presentation.key === 'error' && onRefresh && (
          <Button size="small" variant="outlined" startIcon={<Iconify icon="mdi:refresh" />} onClick={onRefresh}>
            Retry check
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
        {CRM_ONLY_COPY}
      </Typography>

      <Dialog open={mapperOpen} onClose={() => setMapperOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Link existing Dentally patient
          <IconButton size="small" onClick={() => setMapperOpen(false)} aria-label="Close">
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mapperOpen && (
            <DentallyPatientMapper practiceId={practiceId} leadId={leadId} onPatientMapped={handleMapped} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

DentallyPatientLinkSection.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  link: PropTypes.shape({
    dentally_patient_id: PropTypes.number,
    source: PropTypes.string,
    created_at: PropTypes.string,
  }),
  outcome: PropTypes.string,
  isLoading: PropTypes.bool,
  canManage: PropTypes.bool,
  onRefresh: PropTypes.func,
  /** normalisePatientSummary output — null when unlinked or the provider read failed. */
  summary: PropTypes.shape({
    dentallyUrl: PropTypes.string,
    medicalAlert: PropTypes.bool,
    medicalAlertText: PropTypes.string,
    site: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    active: PropTypes.bool,
    archivedReason: PropTypes.string,
    archivedReasonLabel: PropTypes.string,
    archivedReasonIsDeceased: PropTypes.bool,
    nhsNumber: PropTypes.string,
    preferredName: PropTypes.string,
    hasContent: PropTypes.bool,
  }),
  /** ISO timestamp of the last CRM ↔ Dentally comparison. */
  checkedAt: PropTypes.string,
};

DentallyPatientLinkSection.defaultProps = {
  practiceId: null,
  leadId: null,
  link: null,
  outcome: 'idle',
  isLoading: false,
  canManage: false,
  onRefresh: undefined,
  summary: null,
  checkedAt: null,
};
